import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Package, ShoppingCart, Clock, CheckCircle2, MapPin, QrCode,
  ArrowRight, Users, Zap, Route, Filter, Play, Pause, RefreshCw
} from 'lucide-react';

const PickingFulfillment = () => {
  const [pickingMethod, setPickingMethod] = useState('wave'); // batch, wave, directed
  const [activeWave, setActiveWave] = useState(null);
  const [selectedBatch, setSelectedBatch] = useState(null);

  // Mock data for wave picking
  const waves = [
    {
      id: 1,
      name: 'Wave W-2024-001',
      orders: ['ORD-2024-156', 'ORD-2024-157', 'ORD-2024-158'],
      carrier: 'DHL Express',
      pickupTime: 'Today 3:00 PM',
      status: 'active',
      totalItems: 45,
      pickedItems: 12,
      assignedWorkers: ['John Doe', 'Jane Smith'],
      startedAt: '2024-01-28 1:00 PM',
      estimatedCompletion: '2024-01-28 2:30 PM'
    },
    {
      id: 2,
      name: 'Wave W-2024-002',
      orders: ['ORD-2024-159', 'ORD-2024-160'],
      carrier: 'FedEx',
      pickupTime: 'Today 5:00 PM',
      status: 'pending',
      totalItems: 28,
      pickedItems: 0,
      assignedWorkers: [],
      startedAt: null,
      estimatedCompletion: null
    }
  ];

  // Mock data for batch picking
  const batches = [
    {
      id: 1,
      name: 'Batch B-2024-001',
      orders: ['ORD-2024-156', 'ORD-2024-157'],
      worker: 'Mike Wilson',
      status: 'in-progress',
      totalItems: 20,
      pickedItems: 8,
      startedAt: '2024-01-28 1:15 PM',
      items: [
        { sku: 'ELEC-001', name: 'Laptop Dell XPS', qty: 2, location: 'Zone A → Aisle A1 → Rack A1-R1 → Shelf A1-R1-S1 → Bin A1-R1-S1-B1', picked: false },
        { sku: 'ELEC-002', name: 'Monitor 27"', qty: 1, location: 'Zone A → Aisle A1 → Rack A1-R2 → Shelf A1-R2-S1 → Bin A1-R2-S1-B2', picked: true },
        { sku: 'OFF-123', name: 'Printer HP', qty: 1, location: 'Zone A → Aisle A2 → Rack A2-R1 → Shelf A2-R1-S1 → Bin A2-R1-S1-B1', picked: false }
      ]
    },
    {
      id: 2,
      name: 'Batch B-2024-002',
      orders: ['ORD-2024-158'],
      worker: 'Sarah Johnson',
      status: 'pending',
      totalItems: 12,
      pickedItems: 0,
      startedAt: null,
      items: [
        { sku: 'FURN-045', name: 'Office Chair', qty: 1, location: 'Zone B → Aisle B1 → Rack B1-R1 → Shelf B1-R1-S1 → Bin B1-R1-S1-B1', picked: false },
        { sku: 'ELEC-003', name: 'Keyboard', qty: 5, location: 'Zone A → Aisle A1 → Rack A1-R1 → Shelf A1-R1-S2 → Bin A1-R1-S2-B1', picked: false }
      ]
    }
  ];

  // Mock data for directed picking paths (serpentine)
  const directedPaths = [
    {
      id: 1,
      order: 'ORD-2024-156',
      worker: 'John Doe',
      status: 'in-progress',
      path: [
        { step: 1, location: 'Zone A → Aisle A1 → Rack A1-R1 → Shelf A1-R1-S1 → Bin A1-R1-S1-B1', item: 'Laptop Dell XPS', qty: 1, picked: true, distance: 0 },
        { step: 2, location: 'Zone A → Aisle A1 → Rack A1-R1 → Shelf A1-R1-S2 → Bin A1-R1-S2-B1', item: 'Keyboard', qty: 2, picked: true, distance: 15 },
        { step: 3, location: 'Zone A → Aisle A1 → Rack A1-R2 → Shelf A1-R2-S1 → Bin A1-R2-S1-B2', item: 'Monitor 27"', qty: 1, picked: false, distance: 25 },
        { step: 4, location: 'Zone A → Aisle A2 → Rack A2-R1 → Shelf A2-R1-S1 → Bin A2-R1-S1-B1', item: 'Printer HP', qty: 1, picked: false, distance: 45 },
      ],
      totalDistance: 85,
      currentStep: 3,
      startedAt: '2024-01-28 1:30 PM'
    }
  ];

  const getPickingProgress = (picked, total) => {
    return total > 0 ? (picked / total) * 100 : 0;
  };

  const renderWavePicking = () => {
    return (
      <div className="space-y-6">
        {waves.map((wave) => (
          <motion.div
            key={wave.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
          >
            <div className={`p-6 border-b border-gray-200 ${
              wave.status === 'active' ? 'bg-gradient-to-r from-green-50 to-emerald-50' :
              'bg-gradient-to-r from-gray-50 to-slate-50'
            }`}>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">{wave.name}</h3>
                  <div className="mt-2 flex flex-wrap gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <Package className="w-4 h-4 text-gray-500" />
                      <span className="text-gray-600">{wave.orders.length} orders</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <ShoppingCart className="w-4 h-4 text-gray-500" />
                      <span className="text-gray-600">{wave.totalItems} items</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-gray-500" />
                      <span className="text-gray-600">Pickup: {wave.pickupTime}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-gray-500" />
                      <span className="text-gray-600">{wave.assignedWorkers.length} workers</span>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    wave.status === 'active' ? 'bg-green-100 text-green-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {wave.status}
                  </span>
                  {wave.status === 'active' && (
                    <button
                      onClick={() => {
                        setActiveWave(wave.id);
                        alert(`Viewing details for ${wave.name}. Loading wave details...`);
                      }}
                      className="mt-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 text-sm font-medium"
                    >
                      View Details
                    </button>
                  )}
                </div>
              </div>
              {wave.status === 'active' && (
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-600">Progress</span>
                    <span className="text-sm font-semibold text-gray-900">
                      {wave.pickedItems} / {wave.totalItems} items
                    </span>
                  </div>
                  <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-green-500 to-emerald-600 rounded-full transition-all"
                      style={{ width: `${getPickingProgress(wave.pickedItems, wave.totalItems)}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
            {wave.status === 'active' && activeWave === wave.id && (
              <div className="p-6">
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Orders in Wave</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {wave.orders.map((order) => (
                        <div key={order} className="p-3 border border-gray-200 rounded-lg">
                          <p className="font-medium text-gray-900">{order}</p>
                          <button
                            onClick={() => {
                              alert(`Starting picking for order ${order}. This would initialize the picking workflow.`);
                            }}
                            className="mt-2 w-full px-3 py-1.5 bg-orange-600 text-white rounded-lg hover:bg-orange-700 text-sm"
                          >
                            Start Picking
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        ))}
      </div>
    );
  };

  const renderBatchPicking = () => {
    return (
      <div className="space-y-6">
        {batches.map((batch) => (
          <motion.div
            key={batch.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
          >
            <div className={`p-6 border-b border-gray-200 ${
              batch.status === 'in-progress' ? 'bg-gradient-to-r from-blue-50 to-cyan-50' :
              'bg-gradient-to-r from-gray-50 to-slate-50'
            }`}>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">{batch.name}</h3>
                  <div className="mt-2 flex flex-wrap gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-gray-500" />
                      <span className="text-gray-600">{batch.worker}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Package className="w-4 h-4 text-gray-500" />
                      <span className="text-gray-600">{batch.totalItems} items</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <ShoppingCart className="w-4 h-4 text-gray-500" />
                      <span className="text-gray-600">{batch.orders.length} orders</span>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    batch.status === 'in-progress' ? 'bg-blue-100 text-blue-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {batch.status}
                  </span>
                  {batch.status === 'in-progress' && (
                    <button
                      onClick={() => setSelectedBatch(batch.id)}
                      className="mt-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 text-sm font-medium"
                    >
                      Continue Picking
                    </button>
                  )}
                </div>
              </div>
              {batch.status === 'in-progress' && (
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-600">Progress</span>
                    <span className="text-sm font-semibold text-gray-900">
                      {batch.pickedItems} / {batch.totalItems} items
                    </span>
                  </div>
                  <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-blue-500 to-cyan-600 rounded-full transition-all"
                      style={{ width: `${getPickingProgress(batch.pickedItems, batch.totalItems)}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
            {batch.status === 'in-progress' && selectedBatch === batch.id && (
              <div className="p-6">
                <div className="space-y-4">
                  <h4 className="font-semibold text-gray-900">Items to Pick</h4>
                  {batch.items.map((item, index) => (
                    <div
                      key={index}
                      className={`p-4 border-2 rounded-lg ${
                        item.picked
                          ? 'border-green-200 bg-green-50'
                          : 'border-gray-200 bg-white'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h5 className="font-semibold text-gray-900">{item.name}</h5>
                            {item.picked && <CheckCircle2 className="w-5 h-5 text-green-600" />}
                          </div>
                          <p className="text-sm text-gray-600">SKU: {item.sku}</p>
                          <p className="text-sm font-medium text-gray-700 mt-1">Qty: {item.qty}</p>
                        </div>
                        <div className="text-right">
                          <span className="text-xs text-gray-500">Step {index + 1}</span>
                        </div>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg mb-3">
                        <div className="flex items-start gap-2">
                          <MapPin className="w-4 h-4 text-gray-500 mt-0.5" />
                          <p className="text-sm text-gray-700">{item.location}</p>
                        </div>
                      </div>
                      {!item.picked && (
                        <div className="flex gap-2">
                          <button className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center justify-center gap-2 text-sm font-medium">
                            <QrCode className="w-4 h-4" />
                            Scan Bin & Item
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        ))}
      </div>
    );
  };

  const renderDirectedPaths = () => {
    return (
      <div className="space-y-6">
        {directedPaths.map((path) => (
          <motion.div
            key={path.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
          >
            <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-pink-50">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Order {path.order}</h3>
                  <div className="mt-2 flex flex-wrap gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-gray-500" />
                      <span className="text-gray-600">{path.worker}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Route className="w-4 h-4 text-gray-500" />
                      <span className="text-gray-600">{path.totalDistance}m total distance</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Zap className="w-4 h-4 text-gray-500" />
                      <span className="text-gray-600">Optimized path</span>
                    </div>
                  </div>
                </div>
                <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                  {path.status}
                </span>
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div className="mb-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Directed Picking Path (Serpentine)</h4>
                  <p className="text-xs text-gray-600">Optimized sequence to minimize travel distance</p>
                </div>
                {path.path.map((step, index) => (
                  <div
                    key={index}
                    className={`relative p-4 border-2 rounded-lg ${
                      step.picked
                        ? 'border-green-200 bg-green-50'
                        : index === path.currentStep - 1
                        ? 'border-orange-300 bg-orange-50'
                        : 'border-gray-200 bg-white'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                        step.picked
                          ? 'bg-green-500 text-white'
                          : index === path.currentStep - 1
                          ? 'bg-orange-500 text-white'
                          : 'bg-gray-300 text-gray-700'
                      }`}>
                        {step.step}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h5 className="font-semibold text-gray-900">{step.item}</h5>
                            <p className="text-sm text-gray-600 mt-1">Qty: {step.qty}</p>
                          </div>
                          {step.picked && <CheckCircle2 className="w-5 h-5 text-green-600" />}
                        </div>
                        <div className="p-2 bg-white rounded border border-gray-200 mb-2">
                          <div className="flex items-start gap-2">
                            <MapPin className="w-4 h-4 text-gray-500 mt-0.5" />
                            <p className="text-sm text-gray-700">{step.location}</p>
                          </div>
                        </div>
                        {index < path.path.length - 1 && (
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <ArrowRight className="w-4 h-4" />
                            <span>Next: {step.distance}m away</span>
                          </div>
                        )}
                      </div>
                    </div>
                    {index === path.currentStep - 1 && !step.picked && (
                      <div className="mt-3 pt-3 border-t border-orange-200">
                        <button
                          onClick={() => {
                            alert(`Scanning bin "${step.location}" and item "${step.item}" to verify pick. In production, this would verify the scan and update the order status.`);
                          }}
                          className="w-full px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center justify-center gap-2 text-sm font-medium"
                        >
                          <QrCode className="w-4 h-4" />
                          Scan Bin & Item to Verify
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Method Toggle */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Picking & Fulfillment</h2>
            <p className="text-sm text-gray-600 mt-1">Multiple picking methodologies with scan-to-verify</p>
          </div>
        </div>
        <div className="flex gap-2 bg-gray-100 p-1 rounded-lg">
          {[
            { id: 'wave', label: 'Wave Picking', icon: Clock, description: 'Group orders by carrier pickup' },
            { id: 'batch', label: 'Batch Picking', icon: Users, description: 'Pick multiple orders together' },
            { id: 'directed', label: 'Directed Paths', icon: Route, description: 'Optimized serpentine paths' }
          ].map((method) => {
            const Icon = method.icon;
            return (
              <button
                key={method.id}
                onClick={() => setPickingMethod(method.id)}
                className={`flex-1 flex flex-col items-center gap-2 px-4 py-3 rounded-md transition-colors ${
                  pickingMethod === method.id
                    ? 'bg-white text-orange-700 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="font-medium text-sm">{method.label}</span>
                <span className="text-xs text-gray-500">{method.description}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Method Content */}
      {pickingMethod === 'wave' && renderWavePicking()}
      {pickingMethod === 'batch' && renderBatchPicking()}
      {pickingMethod === 'directed' && renderDirectedPaths()}
    </div>
  );
};

export default PickingFulfillment;
